import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getFulfillRfidBadgeOrderDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/user-service-lib/src/wrapper/inputs/rfid-badge-orders/fulfill-rfid-badge-order.form-default-values';
import { FulfillRfidBadgeOrderModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/user-service-lib/src/wrapper/inputs/rfid-badge-orders/fulfill-rfid-badge-order.model';
import React, { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useEmspBoFulfillRfidBadgeOrderMutation } from '@app/core/service/emsp-bo-rfid-badge-order.service';

import {
  FulfillRfidBadgeOrderContextProvider,
  useEmspBoRfidBadgeOrderContext,
} from '../emsp-bo-rfid-badge-order.context';
import { EmspBoRfidBadgeOrderFulfillFormController } from './emsp-bo-rfid-badge-order-fulfill-form/emsp-bo-rfid-badge-order-fulfill-form.controller';

interface EmspBoRfidBadgeOrderFulfillControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const EmspBoRfidBadgeOrderFulfillController = ({
  onCancel,
  onSuccess,
}: EmspBoRfidBadgeOrderFulfillControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const order = useEmspBoRfidBadgeOrderContext();

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [fulfillRfidBadgeOrderMutation] =
    useEmspBoFulfillRfidBadgeOrderMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // DATA FETCHING /////////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<FulfillRfidBadgeOrderModel> = (
    errors,
  ): void => {
    Logger.error(errors);
    toast.error(
      t('$.emsp-bo-rfid-badge-order-fulfill.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    FulfillRfidBadgeOrderModel
  > = (fieldValues): void => {
    setMultiStepFormState(MultiStepFormState.Loading);

    fulfillRfidBadgeOrderMutation(fieldValues)
      .unwrap()
      .then(() => {
        toast.success(t('$.emsp-bo-rfid-badge-order-fulfill.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.emsp-bo-rfid-badge-order-fulfill.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getFulfillRfidBadgeOrderDefaultValues(order);

  return (
    <FulfillRfidBadgeOrderContextProvider data={data}>
      <EmspBoRfidBadgeOrderFulfillFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </FulfillRfidBadgeOrderContextProvider>
  );
};
